import { http, tryHttp } from '@/axios/instance/http';
import { tokenInstance } from '@/axios/instance/token';
import {
  type Invitation,
  type InvitationLoginPayload,
  LOGIN_ROUTE,
  type LoginPayload,
  REFRESH_ROUTE,
  type UserPatchPayload,
  type UserProfile,
} from '@/types';
import type { Locale } from '@/types/company';

export type TenantInfo = {
  schema_name: string;
  company_name: string;
  using_sso: boolean;
  using_mfa: boolean;
};

export const login = (payload: LoginPayload) => {
  const { credential, ...rest } = payload;
  return tryHttp(http.post<{ refresh: string; access: string }>(LOGIN_ROUTE, { ...rest, password: credential }));
};

export const publicLogin = (payload: LoginPayload & { schema_name?: string }) => {
  const { credential, schema_name, ...rest } = payload;
  return tryHttp(
    http.post<{ refresh?: string; access?: string; mfa_required?: boolean }>('/public-login/', {
      ...rest,
      password: credential,
      ...(schema_name && { schema_name }),
    })
  );
};

export const getUserProfile = () => {
  return tryHttp(http.get<UserProfile>('/users/profile/'));
};

export const refreshToken = (payload: { refresh: string }) => {
  return tokenInstance.post<{ refresh: string; access: string }>(REFRESH_ROUTE, payload);
};

export const verifyCredentials = (payload: LoginPayload) => {
  const { credential, ...rest } = payload;
  return tryHttp(http.post<{ detail?: string }>('/users/credentials_verification/', { ...rest, password: credential }));
};

export const fetchUserTenant = (tenant: string) =>
  tryHttp(http.get<{ data?: string; error?: string }>('tenant-lookup/user-tenant', { params: { name: tenant } }));

export const fetchUserTenantLogin = (email: string) =>
  tryHttp(http.get<void>('tenant-lookup/find-tenant-login', { params: { email } }));

export const resetPassword = (email: string) =>
  tryHttp<{ detail?: string }, { detail?: string }>(http.post<{ detail?: string }>('/password_reset/', { email }));

export const tokenValidate = (payload: { token: string }) => {
  return tryHttp<{ detail?: string }, { detail?: string; email?: string[] }>(
    http.post<{ detail?: string }>('/password_reset/validate_token/', payload)
  );
};

export const passwordReset = (payload: { token: string; credential: string }) => {
  const { credential, ...rest } = payload;
  return tryHttp<{ password?: string[]; detail?: string }, { password?: string[]; detail?: string }>(
    http.post<{ password?: string[]; detail?: string }>('/password_reset/confirm/', { ...rest, password: credential })
  );
};

export const verifyMfaToken = (payload: { token: string }, corelationId: string | null) => {
  return tokenInstance.post<{ access_token: string; refresh_token: string }>(
    '/users/mfa_token_verification/',
    payload,
    {
      headers: {
        'X-Correlation-ID': corelationId,
      },
    }
  );
};

export const resendMfaToken = (payload: null, corelationId: string | null) => {
  return tokenInstance.post<void>('/users/resend_mfa_token/', payload, {
    headers: {
      'X-Correlation-ID': corelationId,
    },
  });
};

export const getInvitations = (invitationId: string) => {
  return tryHttp(http.get<Invitation>(`/users/invitations/${invitationId}`));
};

export const invitationLogin = (invitationId: string, payload: InvitationLoginPayload) => {
  return tryHttp(
    http.post<{
      password1?: string[];
      password2?: string[];
      access_token?: string;
      refresh_token?: string;
      locale?: Locale | null;
    }>(`/users/invitations/${invitationId}/login`, payload)
  );
};

export const updateProfileImage = (id: number, image: File) => {
  const formData = new FormData();
  formData.append('avatar', image);

  return tryHttp(
    http.patch(`/users/${id}/update-avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  );
};

export const deleteProfileImage = (id: number) => {
  return tryHttp(http.delete(`/users/${id}/delete-avatar`));
};

export const deleteSelf = (payload: { email: string; password: string }) => {
  return tryHttp(http.post(`/users/delete_self`, payload));
};

export const patchUser = (id: number, data: UserPatchPayload) => {
  return tryHttp(http.patch<UserProfile>(`/users/${id}`, data));
};

export const exchangeSamlAuthCode = (code: string) => {
  return tryHttp(http.post<{ access_token: string; refresh_token: string }>('/saml2/token/', { code }));
};

export const exchangeOidcAuthCode = (code: string) => {
  return tryHttp(http.post<{ access_token: string }>('/oidc/token/', { code }));
};

export const refreshOidcToken = () => {
  return tryHttp(http.post<{ access_token: string }>('/oidc/aim-refresh/', {}));
};

export const revokeOidcToken = () => {
  return tryHttp(http.post<void>('/oidc/aim-revoke/', {}));
};

export const logoutRequest = (refreshTokenValue: string | null) => {
  const payload = refreshTokenValue ? { refresh_token: refreshTokenValue } : {};
  return tryHttp(http.post<void>('/users/logout_request/', payload));
};

export const lookupTenantsByEmail = (email: string) =>
  tryHttp(http.post<{ tenants: TenantInfo[] }>('tenant-lookup/lookup-by-email/', { email }));

export const publicMfaVerification = (payload: { token: string; schema_name: string }, correlationId: string | null) =>
  tokenInstance.post<{ access_token: string; refresh_token: string }>('/public-mfa-verification/', payload, {
    headers: { 'X-Correlation-ID': correlationId },
  });

export const publicSamlLogin = (schemaName: string) =>
  tryHttp(http.post<{ redirect_url: string }>('/public-saml-login/', { schema_name: schemaName }));
